import { hideFeatureBase, showFeatureBase } from "@/integrations/featurebase/utils";
import { unwrapElementRef } from "@/utils/dom/unwrap";
import { onClickOutside } from "@vueuse/core";
import type { UseModals } from "components/modal/hooks/useModals";
import type { IModal } from "components/modal/types";
import { isFunction, lerp, smoothstep } from "math";
import { tv } from "tailwind-variants";
import type { CSSProperties } from "vue";
import type { JSX } from "vue/jsx-runtime";
import { useNavbarSize } from "~/composables/useNavbarSize";
import { useResponsive } from "~/composables/useResponsive";
import { globalDrawers } from "~/composables/useDrawers";

const ModalClass = tv({
  slots: {
    base: "bg-gray-50 md:rounded-lg shadow-xs dark:bg-gray-700 flex flex-col",
    topbar:
      "bg-gray-50 flex items-center justify-between p-4 md:p-5 border-b md:rounded-t-lg dark:border-gray-600 border-gray-200 flex-none",
    content: "p-4 md:p-8 overflow-y-auto pretty-scrollbar flex-auto min-h-0",
    actions:
      "grid items-center p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600 flex-none",
  },
  variants: {
    disableDefaultActionStyles: {
      true: {
        actions:
          "grid p-0 md:p-0 items-center border-t border-gray-200 rounded-b dark:border-gray-600 flex-none",
      },
    },
    disableDefaultContentStyles: {
      true: {
        content: "overflow-y-auto pretty-scrollbar flex-auto min-h-0 p-0 md:p-0",
      },
    },
  },
});

export const Modal = defineComponent<{
  modal: MaybeRef<IModal>;
  hook: UseModals;
}>(
  (props) => {
    const responsive = useResponsive();
    const navbarBounds = useNavbarSize();
    const modal = computed(() => unref(props.modal));
    const title = computed(() => unref(modal.value.title));
    const fullscreen = computed(() => unref(modal.value.fullscreen) === true || responsive.isPhone.value);
    const topbarHidden = computed(() => unref(modal.value.hideTopBar) === true);
    const actionsHidden = computed(() => unref(modal.value.hideActions) === true);
    const disableDefaultActionStyles = computed(
      () => !!unref(modal.value.disableDefaultActionStyles),
    );
    const disableDefaultContentStyles = computed(
      () => !!unref(modal.value.disableDefaultContentStyles),
    );
    const contentStyle = computed(() => unref(modal.value.contentStyle) || {});

    const classes = ModalClass({
      disableDefaultActionStyles: disableDefaultActionStyles.value,
      disableDefaultContentStyles: disableDefaultContentStyles.value,
    });

    // Hide featurebase if this modal is fullscreen (includes small screens)
    if (fullscreen.value) {
      hideFeatureBase()
    }

    const contentRef = ref();
    const showScrollDown = ref(false)
    const getElement = () => unwrapElementRef(contentRef);
    onMounted(() => {
      const el = getElement();
      const modal = unref(props.modal);

      if (!el || !modal) return;

      if (modal.onContentReady) {
        modal.onContentReady(el);
      }
      checkScrollPosition(el);
    });

    const checkScrollPosition = (el: { clientHeight: any; scrollTop: any; scrollHeight: number; }) => {
      const isShorterThanViewport = el.clientHeight >= el.scrollHeight;
      const isAtBottom = isShorterThanViewport || (el.clientHeight + el.scrollTop >= el.scrollHeight - 12);

      showScrollDown.value = !isAtBottom;
    };

    watchEffect(() => {
      const el = getElement();
      if (!el) return;
      const modal = unref(props.modal);

      const handleScroll = () => {
        checkScrollPosition(el);

        if (modal.onScroll) {
          modal.onScroll(el);
        }
      };

      el.addEventListener('scroll', handleScroll);
    });

    onBeforeUnmount(() => {
      // Check if there will be any remaining fullscreen modals after this one unmounts
      const remainingFullscreenModals = Array.from(props.hook.modals.value.values())
        .filter(modalRef => unref(modalRef).id !== modal.value.id) // Exclude current modal
        .some(modalRef => {
          // Check if modal is fullscreen (either explicitly or due to small screen)
          return unref(modalRef).fullscreen === true || responsive.isPhone.value;
        });

      const hasOpenDrawers = globalDrawers.count.value > 0;
      if (!remainingFullscreenModals && !hasOpenDrawers) {
        showFeatureBase()
      }
    })

    onUnmounted(() => {
      // Cleanup - this is now handled in onBeforeUnmount for better timing
    })

    const content = computed((): JSX.Element => {
      const userContent = unref(modal.value.body);
      if (!userContent) return <div />;
      if (isFunction(userContent)) return <userContent />;
      return userContent;
    });
    const actions = computed((): JSX.Element | null => {
      const userActions = unref(modal.value.actions);
      if (!userActions) return null;
      if (isFunction(userActions)) return <userActions />;
      return userActions;
    });

    const animationValue = computed((): number =>
      unref(modal.value.animation.current),
    );
    const opacity = computed(() =>
      lerp(0.0, 100.0, smoothstep(0.0, 1.0, animationValue.value)),
    );

    const alpha = computed(() =>
      lerp(0.0, 0.75, smoothstep(0.0, 1.0, animationValue.value)),
    );

    const scale = computed(() =>
      lerp(0.25, 1.0, smoothstep(0.0, 1.0, animationValue.value)),
    );

    const userStyle = computed(
      (): CSSProperties =>
        unref(modal.value.style) || {
          maxWidth: "70svw",
          maxHeight: "60svh",
        },
    );

    const style = computed((): CSSProperties[] => {
      // Calculate z-index based on position in modal stack
      const items = props.hook.items.value;
      const modalIndex = items.findIndex(m => unref(m)?.id === modal.value.id);
      const baseZIndex = 500;
      const contentZIndex = baseZIndex + (modalIndex * 2) + 1; // Content is always above its backdrop

      return [
        {
          opacity: `${opacity.value}%`,
          pointerEvents: "all",
          position: "fixed",
          left: "0px",
          top: "0px",
          right: "0px",
          bottom: "0px",
          margin: "auto",
          ...(scale.value < 0.99999 ? {
            scale: `${scale.value}`
          } : {}),
          zIndex: contentZIndex,
        },
        responsive.isPhone.value ? {} : userStyle.value,
        fullscreen.value || responsive.isPhone.value
          ? {
            maxWidth: "100svw",
            width: "100svw",
            ...(responsive.isPhone.value ? {
              flex: 'none',
              height: `calc(100svh - ${navbarBounds.height.value}px)`,
              maxHeight: '100svh',
              minHeight: '100svh',
            } : {
              maxHeight: "100svh",
              height: "100svh",
            })
          }
          : {
            width: 'clamp(100px,70svw,1920px)',
            ...userStyle.value,
          },
      ];
    });

    const backdropStyle = computed((): CSSProperties => {
      // Calculate z-index based on position in modal stack
      const items = props.hook.items.value;
      const modalIndex = items.findIndex(m => unref(m)?.id === modal.value.id);
      const baseZIndex = 500;
      const zIndex = baseZIndex + (modalIndex * 2); // Each modal gets backdrop at z, content at z+1

      return {
        backgroundColor: `rgba(0, 0, 0, ${alpha.value})`,
        pointerEvents: 'all',
        zIndex: zIndex,
      }
    })

    const handleClose = () => {
      props.hook.pop(modal.value.id);
    };

    const modalRef = ref();

    const closeOnClickOutside = computed(() => unref(modal.value.closeOnClickOutside) ?? true);

    onClickOutside(
      modalRef,
      () => {
        if (!closeOnClickOutside.value) return;

        // Only close if this is the topmost modal in the stack
        const items = props.hook.items.value;
        const topmostModalRef = items[items.length - 1];
        const topmostModal = unref(topmostModalRef); // Need to unwrap the ref!
        if (topmostModal?.id !== modal.value.id) return;

        props.hook.pop(modal.value.id);
      },
      { ignore: [".virtual-window", ".toast"] },
    );

    return () => {
      return (
        <div
          class="w-full h-full fixed top-0 left-0"
          style={backdropStyle.value}
          onClick={(e) => {
            e.preventDefault()

            // Check if click is on backdrop (not on modal content)
            if (e.target === e.currentTarget && closeOnClickOutside.value) {
              const items = props.hook.items.value;
              const topmostModalRef = items[items.length - 1];
              const topmostModal = unref(topmostModalRef);
              if (topmostModal?.id === modal.value.id) {
                props.hook.pop(modal.value.id);
              }
            }
          }}
        >
          <div class={classes.base()} style={style.value} ref={modalRef}>
            {!topbarHidden.value && (
              <div class={classes.topbar()}>
                <h3 class={[
                  "font-semibold text-gray-900 dark:text-white",
                  responsive.isPhone.value ? "text-sm" : "text-xl"
                ]}>
                  {title.value}
                </h3>
                <button
                  type="button"
                  class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                  data-modal-hide="static-modal"
                  onClick={handleClose}
                >
                  <svg
                    class="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span class="sr-only">Close modal</span>
                </button>
              </div>
            )}
            <div class={[classes.content(), responsive.isPhone.value ? "text-xs" : "text-sm"]}
              style={contentStyle.value} ref={contentRef}>
              {content.value}
            </div>

            {actions.value && !actionsHidden.value && (
              <div class={classes.actions()}>
                {actions.value}
              </div>
            )}
          </div>
        </div>
      );
    };
  },
  { props: ["modal", "hook"] },
);
